import { computed, inject, Injectable, signal } from '@angular/core';
import { finalize } from 'rxjs/operators';
import { CreditUsageResponse, Usage } from '../../features/usage/service/usage';

export type CreditRemainingLevel = 'success' | 'warning' | 'danger';

@Injectable({ providedIn: 'root' })
export class CreditUsageStore {
  private readonly api = inject(Usage);

  readonly loading = signal(false);
  readonly loaded = signal(false);
  readonly creditInfo = signal<CreditUsageResponse | null>(null);

  readonly remainingCredits = computed(() => this.creditInfo()?.remaining_credits ?? null);

  readonly remainingLevel = computed((): CreditRemainingLevel => {
    const remaining = this.remainingCredits();
    if (remaining === null || remaining >= 1000) return 'success';
    if (remaining < 500) return 'danger';
    return 'warning';
  });

  readonly showInHeader = computed(() => this.loaded() && this.remainingCredits() !== null);

  ensureLoaded(): void {
    if (this.loaded() || this.loading()) return;
    this.load();
  }

  refresh(): void {
    if (this.loading()) return;
    this.load();
  }

  private load(): void {
    this.loading.set(true);
    this.api
      .getCreditUsageInfo()
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe((data) => {
        this.creditInfo.set(data);
        this.loaded.set(true);
      });
  }
}
